'use client'
import React, { useContext, useState, useEffect } from 'react'
import { useInView } from "react-intersection-observer";

import { BookmarkAPI, SocializeAPI } from '@/library/entities/socialize/data/socialize'
import { Angry, CircleX, Frown, Heart, HeartCrackIcon, HeartIcon, Laugh, MessageCirclePlus, PartyPopper, ThumbsUp } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { toast } from "sonner"
import { UserContext } from '@/library/entities/core/user/contexts/UserContext'
import Styling from '@/library/styling'
import LoginModal from '@/library/entities/core/user/components/authentication/LoginModal'
import { FaSpinner } from 'react-icons/fa'
import { COUNTRY_UAE_ID, COUNTRY_ID } from '@/data/constants';
import { translateIt } from '@/data/translations';


export default function Bookmark(props) {
  const {
    appName,
    modelName,
    entityId,
    countPlacement = 'left',
    className = ''
  } = props
  const { ref, inView, entry } = useInView({
    threshold: 0,
    triggerOnce: true
  });

  const [openLoginModal, setOpenLoginModal] = useState(false)
  const { bookmarkList, getAndSetBookmarks, isAuthenticated } = useContext(UserContext)
  const [allBookmarksList, setAllBookmarkedList] = useState([])
  const [isLoadingAllBookmarkList, setIsLoadingAllBookmarkList] = useState(true)
  const [currentBookmark, setCurrentBookmark] = useState(null)
  const emotionWrapperClasses = 'hover:scale-[2.0] rounded-full p-2 cursor-pointer transition-all duration-300'
  const emotionClasses = 'h-4 w-4 md:h-3 md:w-3'
  const emotions = [
    // {
    //   id: 0,
    //   name: 'Default',
    //   icon: <CircleX />
    // },
    {
      id: 1,
      name: 'Like',
      classesDefault: 'text-[blue] bg-white',
      classesActive: 'text-white bg-blue-500',
      icon: <ThumbsUp className={`${emotionClasses}`} />
    },
    {
      id: 2,
      name: 'Love',
      classesDefault: 'text-[red] bg-white',
      classesActive: 'text-white bg-red-500',
      icon: <Heart className={`${emotionClasses}`} />,
    },
    // {
    //   id: 3,
    //   name: 'Haha',
    //   classesDefault: 'text-[pink] bg-white',
    //   classesActive: 'text-white bg-pink-500',
    //   icon: <Laugh className={`${emotionClasses}`} />
    // },
    // {
    //   id: 4,
    //   name: 'Wow',
    //   classesDefault: 'text-[green] bg-white',
    //   classesActive: 'text-white bg-green-500',
    //   icon: <PartyPopper className={`${emotionClasses}`} />
    // },
    {
      id: 5,
      name: 'Sad',
      classesDefault: 'text-[purple] bg-white',
      classesActive: 'text-white bg-purple-500',
      icon: <Frown className={`${emotionClasses}`} />
    },
    // {
    //   id: 6,
    //   name: 'Angry',
    //   classesDefault: 'text-[orange] bg-white',
    //   classesActive: 'text-white bg-orange-500',
    //   icon: <Angry className={`${emotionClasses}`} />
    // },
  ]

  const getBookmarkedList = async () => {
    setIsLoadingAllBookmarkList(true)
    const bookmarkedList = await BookmarkAPI.getForEntity({ appName, modelName, entityId })
    setAllBookmarkedList(bookmarkedList)
    setIsLoadingAllBookmarkList(false)
  }

  useEffect(() => {
    if (inView) {
      const connectedToKind = SocializeAPI.getConnectedToKind(appName, modelName)
      const entityKey = SocializeAPI.getEntityKey(connectedToKind)
      const currentBookmark_ = bookmarkList.find(bookmark => {
        return bookmark.connected_to_kind === connectedToKind && bookmark[entityKey] === parseInt(entityId)
      })
      setCurrentBookmark(currentBookmark_)
    }
  }, [bookmarkList, inView, entityId])

  useEffect(() => {
    if (inView) {
      getBookmarkedList()
    }
  }, [inView])


  const handleOnClick = async (e, emotionId) => {
    e.preventDefault()
    if (!isAuthenticated) {
      setOpenLoginModal(!!!openLoginModal)
      toast.error(<>
        {translateIt(COUNTRY_ID, 'login_to_react')}
        <LoginModal
          countryId={COUNTRY_ID}
          size="sm"
          trigger={
            <div className={`underline cursor-pointer ${Styling.sizes.sm.strong} `}>
              {translateIt(COUNTRY_ID, 'login__login')}
            </div>
          }
        />
      </>)
      return
    }
    console.log('shoud be loading')
    setIsLoadingAllBookmarkList(true)
    const res = await BookmarkAPI.create({ appName, modelName, entityId, emotionId })
    const isCreated = res.is_active
    await getAndSetBookmarks()
    await getBookmarkedList()
    if (isCreated) {
      toast.success(translateIt(COUNTRY_ID, 'bookmark__reactionAdded'))
    } else {
      toast.info(translateIt(COUNTRY_ID, 'bookmark__reactionRemoved'))
    }
    setIsLoadingAllBookmarkList(false)
    console.log('loading completed')
  }

  const renderReactionCount = () => {
    const reactionCount = allBookmarksList?.length
    const reactionCountText = reactionCount === 1 ? translateIt(COUNTRY_ID, 'bookmark__reaction') : translateIt(COUNTRY_ID, 'bookmark__reactions')
    return (
      <>
        {isLoadingAllBookmarkList &&
          <div className="flex flex-row items-center gap-1">
            <FaSpinner className="h-3 w-3 animate-spin" />
          </div>
        }
        {reactionCount > 0 && (
          <div className="text-primary-400 text-xs text-left">
            {!isLoadingAllBookmarkList && reactionCount > 0 &&
              `${reactionCount} ${reactionCountText}`
            }
          </div>
        )}
      </>
    )
  }

  return (
    <div ref={ref} className={`flex flex-row items-center gap-2 ${className}`}>
      {/* <LoginModal countryId={COUNTRY_ID} triggerOpen={openLoginModal} size="sm" /> */}
      {countPlacement === 'left' && renderReactionCount()}
      <div className="flex flex-row items-center gap-3 md:gap-1">
        {emotions.map((emotion, index) => {
          const isBookmarked = currentBookmark?.emotion === emotion.id
          const zIndex = 50 - index * 10
          const bookmarkedClasses = isBookmarked ? `${emotion.classesActive} opacity-100` : `${emotion.classesDefault} opacity-40 hover:opacity-100`
          return (
            <TooltipProvider key={emotion.id}>
              <Tooltip>
                <TooltipTrigger aria-label={emotion.name}>
                  <div
                    className={`${emotionWrapperClasses} ${zIndex} hover:z-50 ${bookmarkedClasses}`}
                    onClick={e => handleOnClick(e, emotion.id)}
                  >
                    {emotion.icon}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {emotion.name}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
        {/* <div className={`text-xs ${Styling.buttons.sm.primary.default} w-auto`}>
          <MessageCirclePlus className="h-4 w-4" /> 
        </div> */}
        {countPlacement === 'right' && renderReactionCount()}
      </div>
    </div>
  )
}
